import React, { useEffect, useMemo, useState } from 'react';

import { ApolloProvider, gql } from '@apollo/client';
import { CacheLocation } from '@auth0/auth0-spa-js';
import { StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import SwiperCore, { Navigation, Pagination, Mousewheel } from 'swiper';

import createApolloClient from '../src/graphql';
import { AUTH_APP_ID_KEY, ELOD_REFERENT } from './const';
import { CommonLayout } from './containers/CommonLayout';
import AlertProvider, { Severity, useAlert } from './context/AlertProvider';
import { AuthProvider } from './context/AuthProvider';
import { DiscoverController } from './context/DiscoverContext';
import { OnlineContentConnectorController } from './context/OnlineContentConnectorContext';
import { RXDController } from './context/RXDContext';
import { ScriptsProvider } from './context/ScriptLoader/useScriptLoader';
import { SsrProvider } from './context/SsrContext';
import i18n from './i18n';
import theme from './theme';
import { handleGraphQlError } from './utils/helpers';
import { useAuth } from './utils/hooks';
import 'swiper/swiper-bundle.css';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

declare global {
  interface Window {
    UE?: {
      pageHit?: (data: object) => void;
      resetAuth: (data: object) => void;
      destroy?: () => void;
    };
    userengage?: (eventName: string, data?: any) => Promise<any>;
    invitePageCustomTexting?: {
      [locale: string]: {
        title?: string;
        description?: string;
        signUpBtn?: string;
      };
    };
    triggerFreeTrialSignUp?: boolean;
    isSSR?: boolean;
  }
}

const ROUTERS = {
  server: StaticRouter,
  client: BrowserRouter,
};

SwiperCore.use([Navigation, Pagination, Mousewheel]);

const getApolloClient = (type, client, handleError, getToken) => {
  if (type === 'server') {
    return client;
  }
  return createApolloClient(handleError, undefined, undefined, undefined, getToken);
};

export function ApolloLoader({ type = 'client', client, host, children }) {
  const { isAuthenticated, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const apolloClient = useMemo(() => {
    const handleError = (error, operation) => {
      handleGraphQlError(error, operation);

      showAlert({ message: t('error.errorHappened'), severity: Severity.Error });
    };

    return getApolloClient(
      type,
      client,
      handleError,
      isAuthenticated
        ? async () => {
            try {
              return await getAccessTokenSilently();
            } catch (e) {
              try {
                return await getAccessTokenWithPopup();
              } catch (e) {}
            }
          }
        : null,
    );
  }, [
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    client,
    type,
    isAuthenticated,
    showAlert,
    t,
  ]);

  apolloClient.writeQuery({
    query: gql`
      query GetHasNewsSetup {
        hasNews
      }
    `,
    data: {
      hasNews: !host?.includes(ELOD_REFERENT),
    },
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}

const StyledEngineProviderOnlyForDevMode = ({ children }) =>
  process.env.NODE_ENV === 'development' ? (
    <StyledEngineProvider injectFirst>{children}</StyledEngineProvider>
  ) : (
    children
  );

function App({
  type,
  client = undefined,
  routerContext,
  location,
  hasAuthenticatedCookie,
  redirect = undefined,
  host = '',
}) {
  const Router = ROUTERS[type];
  const [authAppId, setAuthAppId] = useState('');

  useEffect(() => {
    const appId = localStorage.getItem(AUTH_APP_ID_KEY);
    if (appId) {
      setAuthAppId(appId);
    }
  }, []);

  return (
    <SsrProvider redirect={redirect}>
      <ScriptsProvider>
        <RXDController>
          <DiscoverController>
            <OnlineContentConnectorController>
              <Router context={routerContext} location={location}>
                <AuthProvider
                  domain={process.env.REACT_APP_AUTH0_DOMAIN || ''}
                  cacheLocation={'localstorage' as CacheLocation}
                  audience={process.env.REACT_APP_AUTH0_AUDIENCE || ''}
                  scope="has-subscription"
                  useRefreshTokens
                  clientId={authAppId}
                >
                  <StyledEngineProviderOnlyForDevMode>
                    <ThemeProvider theme={theme}>
                      <I18nextProvider i18n={i18n}>
                        <AlertProvider>
                          <ApolloLoader type={type} client={client} host={host}>
                            <CommonLayout
                              setAuthAppId={setAuthAppId}
                              hasAuthenticatedCookie={hasAuthenticatedCookie}
                            />
                          </ApolloLoader>
                        </AlertProvider>
                      </I18nextProvider>
                    </ThemeProvider>
                  </StyledEngineProviderOnlyForDevMode>
                </AuthProvider>
              </Router>
            </OnlineContentConnectorController>
          </DiscoverController>
        </RXDController>
      </ScriptsProvider>
    </SsrProvider>
  );
}

export default App;
